// The mission HUD: what your spies are doing, what you hold, and what has
// been taken from you.
//
// Everything here is read off the snapshot and sent back as an intent. The
// sim decides: which cities a spy may watch, whether a ransom may be paid,
// whether a rescue is possible. The tab only shows what the sim says and
// asks.

import type { PlayerHudEntry } from '@aod/ui';
import { SceneImage } from '@aod/ui/components/ui/scene';
import { useState } from 'react';
import { type Capture, type Dynasty, mayPayRansom, mayRescue } from '../../dynasty/dynasty.js';
import { type Espionage, type SpyIntel, spiesAt, spiesOf } from '../../espionage/espionage.js';
import { type HeroCaptivity, captivesHeld, captivesTaken } from '../../heroes/captivity.js';
import { type HeroQuests, activeQuests } from '../../heroes/quests.js';
import { useT } from '../../i18n/index.js';
import { appDom } from '../../lib/appDom.js';
import type { CaptureChoice } from '../../sim/useSimWorld/applyIntent.js';
import type { OpsActions } from '../../sim/useSimWorld/useEspionageIntents.js';
import { CaptorDialog } from '../Modal/CaptorDialog.js';
import { RoyalPortrait } from '../RoyalPortrait.js';
import { sceneUrls } from '../scenes.js';
import { HeldAgents } from './HeldAgents.js';
import { HeroHeldSection, HeroTakenSection } from './HeroCaptivitySections.js';
import { HeroQuestSection } from './HeroQuestSections.js';
import { SpyCard } from './SpyCard.js';
import { decisionLabel } from './captureDecision.js';

export interface MissionsTabProps {
  /** The spies of the local house, while `espionage.enabled` is on. */
  readonly espionage?: Espionage | null;
  /** The royal families, while `diplomacy.dynasty` is on. */
  readonly dynasty?: Dynasty | null;
  /** The heroes in captivity, while `heroes.captivity` is on. */
  readonly heroCaptivity?: HeroCaptivity | null;
  /** The orders your heroes carry, while `heroes.quests` is on. */
  readonly heroQuests?: HeroQuests | null;
  readonly localPlayerIndex: number;
  readonly players: readonly PlayerHudEntry[];
  /** Send a spy to watch an enemy city. */
  readonly onDispatchSpy?: (unitId: string, targetCityId: string) => boolean;
  /** The hostile orders (`espionage.ops`): sabotage, poison, assassination. */
  readonly opsActions?: OpsActions;
  /** Decide what happens to a royal you hold. */
  readonly onChooseCapture?: (royalId: number, choice: CaptureChoice) => boolean;
  /** Walk a royal out of where they are kept. */
  readonly onSpyRescue?: (unitId: string, royalId: number) => boolean;
  /** Take the place a royal is kept by force. */
  readonly onRaidRescue?: (royalId: number) => boolean;
  /** Buy a royal back. */
  readonly onPayRansom?: (royalId: number) => boolean;
  /** Decide what happens to a hero you hold. */
  readonly onChooseHeroCaptivity?: (unitId: string, choice: CaptureChoice) => boolean;
  /** Buy a hero of your house back. */
  readonly onPayHeroRansom?: (unitId: string) => boolean;
}

export function MissionsTab({
  espionage,
  dynasty,
  heroCaptivity,
  heroQuests,
  localPlayerIndex,
  players,
  onDispatchSpy,
  opsActions,
  onChooseCapture,
  onSpyRescue,
  onRaidRescue,
  onPayRansom,
  onChooseHeroCaptivity,
  onPayHeroRansom,
}: MissionsTabProps) {
  const t = useT();
  const houseName = (seat: number) =>
    players.find((p) => p.playerId === seat)?.name ?? `#${seat + 1}`;
  const rank = (female: boolean) => (female ? 'dynasty.princess' : 'dynasty.prince');
  const nameOf = (royalId: number) => {
    const royal = dynasty?.royals.find((r) => r.id === royalId);
    if (!royal) return `#${royalId}`;
    return t('dynasty.ofHouse', {
      rank: t(rank(royal.female)),
      house: houseName(royal.seat),
    });
  };

  const spies = spiesOf(espionage ?? null);
  const captures = dynasty?.captures ?? [];
  const held = captures.filter((c) => c.captor === localPlayerIndex);
  // A captivity that concerns you: a royal of your house, or one married
  // into it — the same rule the sim uses to decide who may rescue or buy.
  const taken = dynasty
    ? captures.filter((c) => mayRescue(dynasty, localPlayerIndex, c.royal))
    : [];

  // The heroes in chains (`heroes.captivity`). A captive is off the board,
  // so the mission tab's two hero lists are where their absence is shown.
  const heroHeld = captivesHeld(heroCaptivity ?? null, localPlayerIndex);
  const heroTaken = captivesTaken(heroCaptivity ?? null, localPlayerIndex);

  const nothingToDo =
    spies.length === 0 &&
    (espionage?.captives.length ?? 0) === 0 &&
    held.length === 0 &&
    taken.length === 0 &&
    heroHeld.length === 0 &&
    heroTaken.length === 0 &&
    activeQuests(heroQuests ?? null).length === 0;

  return (
    <div className="flex flex-col gap-3" data-testid="missions-tab">
      {/* --- your spies -------------------------------------------------- */}
      <section className="flex flex-col gap-1.5">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {t('espionage.spies')}
        </h3>
        <SceneImage
          {...sceneUrls('spy', spies.some((s) => s.intel) ? 'spy-report' : 'spy-infiltrate')}
          widths={[384, 768]}
          sizes="280px"
          aspect="strip"
          data-testid="spies-art"
        />
        {spies.length === 0 ? (
          <p className="text-[10px] text-muted-foreground">{t('espionage.noSpies')}</p>
        ) : (
          spies.map((spy) => (
            <SpyCard
              key={spy.unitId}
              spy={spy}
              espionage={espionage}
              players={players}
              localPlayerIndex={localPlayerIndex}
              nameOf={nameOf}
              onDispatchSpy={onDispatchSpy}
              opsActions={opsActions}
            />
          ))
        )}
      </section>

      {opsActions && espionage && espionage.captives.length > 0 && (
        <HeldAgents
          agents={espionage.captives}
          ransom={espionage.counter?.ransom ?? 0}
          nameOf={houseName}
          actions={opsActions}
        />
      )}

      {/* --- royals you hold ---------------------------------------------- */}
      {held.length > 0 && (
        <section className="flex flex-col gap-1.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {t('espionage.captives')}
          </h3>
          {held.map((capture) => (
            <CaptiveRow
              key={capture.royal}
              capture={capture}
              name={nameOf(capture.royal)}
              portrait={dynasty?.royals.find((r) => r.id === capture.royal)?.portrait ?? null}
              onChoose={onChooseCapture}
            />
          ))}
        </section>
      )}

      {/* --- royals taken from you ---------------------------------------- */}
      {taken.length > 0 && (
        <section className="flex flex-col gap-1.5">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {t('espionage.taken')}
          </h3>
          {taken.map((capture) => {
            const rescuers = spiesAt(espionage ?? null, capture.at.q, capture.at.r);
            const canPay = dynasty ? mayPayRansom(dynasty, localPlayerIndex, capture.royal) : false;
            const canRescue = dynasty ? mayRescue(dynasty, localPlayerIndex, capture.royal) : false;
            return (
              <div
                key={capture.royal}
                className="flex flex-col gap-1 rounded-md border border-border/50 bg-card/60 px-2.5 py-2"
              >
                <div className="flex items-center gap-2 text-xs">
                  <RoyalPortrait
                    portrait={dynasty?.royals.find((r) => r.id === capture.royal)?.portrait ?? null}
                    label={nameOf(capture.royal)}
                    stage={dynasty?.royals.find((r) => r.id === capture.royal)?.stage}
                    className="h-8 w-6"
                  />
                  <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                    {nameOf(capture.royal)}
                  </span>
                  <span className="shrink-0 text-[10px] text-muted-foreground">
                    {t('espionage.heldSince', { n: capture.since })}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {capture.choice === 'ransom' && canPay && onPayRansom && (
                    <button
                      type="button"
                      data-testid={`pay-ransom-${capture.royal}`}
                      {...appDom({
                        id: `pay-ransom-${capture.royal}`,
                        type: 'Button',
                        role: 'button',
                        scope: 'section',
                      })}
                      onClick={() => onPayRansom(capture.royal)}
                      className="rounded border border-amber-300/50 px-2 py-0.5 text-[10px] text-amber-100 hover:bg-amber-300/10"
                    >
                      {t('espionage.payRansom')} ({capture.price})
                    </button>
                  )}
                  {canRescue &&
                    onSpyRescue &&
                    rescuers.map((spy) => (
                      <button
                        key={spy.unitId}
                        type="button"
                        data-testid={`spy-rescue-${spy.unitId}-${capture.royal}`}
                        {...appDom({
                          id: `spy-rescue-${spy.unitId}-${capture.royal}`,
                          type: 'Button',
                          role: 'button',
                          scope: 'section',
                        })}
                        onClick={() => onSpyRescue(spy.unitId, capture.royal)}
                        className="rounded border border-border/60 px-2 py-0.5 text-[10px] hover:bg-muted/60"
                      >
                        {t('espionage.rescue')}
                      </button>
                    ))}
                  {canRescue && onRaidRescue && (
                    <button
                      type="button"
                      data-testid={`raid-rescue-${capture.royal}`}
                      {...appDom({
                        id: `raid-rescue-${capture.royal}`,
                        type: 'Button',
                        role: 'button',
                        scope: 'section',
                      })}
                      onClick={() => onRaidRescue(capture.royal)}
                      className="rounded border border-border/60 px-2 py-0.5 text-[10px] hover:bg-muted/60"
                    >
                      {t('espionage.raid')}
                    </button>
                  )}
                  {capture.rescues > 0 && (
                    <span className="self-center text-[10px] text-muted-foreground">
                      {t('espionage.alreadyTried')}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </section>
      )}

      {/* --- heroes you hold ----------------------------------------------- */}
      <HeroHeldSection
        heroCaptivity={heroCaptivity}
        localPlayerIndex={localPlayerIndex}
        onChooseHeroCaptivity={onChooseHeroCaptivity}
      />

      {/* --- heroes taken from you ----------------------------------------- */}
      <HeroTakenSection
        heroCaptivity={heroCaptivity}
        localPlayerIndex={localPlayerIndex}
        onPayHeroRansom={onPayHeroRansom}
      />

      {/* --- the orders your heroes carry ---------------------------------- */}
      <HeroQuestSection heroQuests={heroQuests} />

      {nothingToDo && <p className="text-[10px] text-muted-foreground">{t('espionage.empty')}</p>}
    </div>
  );
}

/** A royal in your keeping, and the decision you have made about them. */
function CaptiveRow({
  capture,
  name,
  portrait,
  onChoose,
}: {
  readonly capture: Capture;
  readonly name: string;
  readonly portrait: string | null;
  readonly onChoose?: (royalId: number, choice: CaptureChoice) => boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border/50 bg-card/60 px-2.5 py-2">
      <div className="flex items-center gap-2 text-xs">
        <RoyalPortrait portrait={portrait} label={name} className="h-8 w-6" />
        <span className="min-w-0 flex-1 truncate font-medium text-foreground">{name}</span>
        <span className="shrink-0 text-[10px] text-muted-foreground">
          {t(decisionLabel(capture.choice))}
        </span>
      </div>
      {onChoose && (
        <button
          type="button"
          data-testid={`captor-decide-${capture.royal}`}
          {...appDom({
            id: `captor-decide-${capture.royal}`,
            type: 'Button',
            role: 'button',
            scope: 'section',
          })}
          onClick={() => setOpen(true)}
          className="self-start rounded border border-amber-300/50 px-2 py-0.5 text-[10px] text-amber-100 hover:bg-amber-300/10"
        >
          {t('espionage.decision')}
        </button>
      )}
      {open && (
        <CaptorDialog
          captiveName={name}
          current={capture.choice}
          onChoose={(choice) => {
            onChoose?.(capture.royal, choice);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
